import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, CheckCircle, AlertCircle } from 'lucide-react';
import { Translations } from '../types';
import SpotlightCard from './SpotlightCard';

interface WaitlistProps {
  t: Translations;
  isVisible: boolean;
  onClose: () => void;
}

export const Waitlist: React.FC<WaitlistProps> = ({ t, isVisible, onClose }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateEmail(email)) {
      setStatus('error');
      return;
    }

    try {
      const userAgent = navigator.userAgent;
      const isMobile = /Mobi|Android|iPhone/i.test(userAgent);
      const res = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          browser: userAgent,
          isMobile,
        }),
      });

      if (res.ok) {
        setStatus('success');
        setTimeout(() => {
          onClose();
          setEmail('');
          setStatus('idle');
        }, 2000);
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  if (!isVisible) return null;

  return (
    <motion.div
      className="waitlist-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.8, y: 50 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.8, y: 50 }}
        onClick={(e) => e.stopPropagation()}
      >
        <SpotlightCard
          className="waitlist-modal"
          spotlightColor="rgba(0, 255, 157, 0.1)"
        >
          <button className="close-button" onClick={onClose}>×</button>

          <div className="waitlist-content">
          <motion.div
            className="waitlist-icon"
            animate={{ rotate: status === 'success' ? 360 : 0 }}
            transition={{ duration: 0.5 }}
          >
            {status === 'success' ? (
              <CheckCircle size={60} className="success-icon" />
            ) : status === 'error' ? (
              <AlertCircle size={60} className="error-icon" />
            ) : (
              <Mail size={60} />
            )}
          </motion.div>

          <h3 className="waitlist-title">{t.waitlist.title}</h3>
          <p className="waitlist-subtitle">{t.waitlist.subtitle}</p>

          {status === 'success' ? (
            <motion.div
              className="success-message"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              {t.waitlist.successMessage}
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="waitlist-form">
              <div className="input-group">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.waitlist.emailPlaceholder}
                  className={status === 'error' ? 'error' : ''}
                />
                <motion.button
                  type="submit"
                  className="submit-button"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  disabled={false}
                >
                  {t.waitlist.submitButton}
                </motion.button>
              </div>
              {status === 'error' && (
                <motion.p
                  className="error-message"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {t.waitlist.errorMessage}
                </motion.p>
              )}
            </form>
          )}
        </div>
        </SpotlightCard>
      </motion.div>
    </motion.div>
  );
}; 